import { useEffect } from 'react';

import { createHook, createStore, type StoreActionApi } from 'react-sweet-state';

import ProfileClient from '@atlaskit/profilecard/client';
import { useTeamCentralStore } from '@atlassian/ptc-common';
import { getConfig } from '@atlassian/teams-common/context-config';

import { useAppConfig } from '../components/AppConfigContext';

interface State {
	profileClient?: ProfileClient;
	enableTeamCentral: boolean;
	cloudId: string;
}

type StoreApi = StoreActionApi<State>;
type Actions = typeof actions;

const CACHE_MAX_AGE = 60 * 1000;

function currentClientMatchesConfig(
	state: State,
	enableTeamCentral: boolean,
	cloudId: string,
	// Using a type guard on the return here to indicate that if this function
	// returns true then the profileClient on it must be defined.
): state is Required<State> {
	if (!state.profileClient) {
		return false;
	}

	return state.enableTeamCentral === enableTeamCentral && state.cloudId === cloudId;
}

const actions = {
	getClient:
		() =>
		({ getState, dispatch }: StoreApi): ProfileClient => {
			const currentClient = getState().profileClient;

			if (currentClient) {
				return currentClient;
			}

			return dispatch(actions.createClient());
		},

	createClient:
		(enableTeamCentral = false, cloudId: string = 'None', productName: string = '') =>
		({ getState, setState }: StoreApi): ProfileClient => {
			const currentState = getState();

			if (currentClientMatchesConfig(currentState, enableTeamCentral, cloudId)) {
				return currentState.profileClient;
			}

			// Immediately save config to avoid re-create loop
			setState({ enableTeamCentral, cloudId });
			const { stargateRoot } = getConfig();
			const profileClient = new ProfileClient({
				gatewayGraphqlUrl: `${stargateRoot}/graphql`,
				url: `${stargateRoot}/directory/graphql`,
				teamCentralUrl: enableTeamCentral ? `${stargateRoot}/watermelon/graphql` : undefined,
				teamCentralBaseUrl: enableTeamCentral ? getConfig().teamCentralUrl : undefined,
				cacheSize: 20,
				cacheMaxAge: CACHE_MAX_AGE,
				cloudId,
				productIdentifier: productName.toLowerCase(),
			});

			setState({ profileClient });

			return profileClient;
		},
};

const ProfileClientStore = createStore<State, Actions>({
	initialState: { enableTeamCentral: false, cloudId: 'None' },
	actions,
});

const useProfileClientState = createHook(ProfileClientStore);

export const useProfileClient = (): ProfileClient => {
	const [{ hasWorkspace, orgHasWorkspace }] = useTeamCentralStore();
	const [state, { getClient, createClient }] = useProfileClientState();
	const { cloudId, productName } = useAppConfig();

	const enableTeamCentral = hasWorkspace || orgHasWorkspace;

	useEffect(() => {
		if (!currentClientMatchesConfig(state, enableTeamCentral, cloudId)) {
			// re-create the client
			createClient(enableTeamCentral, cloudId, productName);
		}
	}, [createClient, enableTeamCentral, state, cloudId, productName]);

	return getClient();
};
